import React, { useRef, useState, useEffect, useLayoutEffect, useContext } from "react";
import { CameraSessionsContext } from '../context/CameraSessionsContext';
import { createCameraSessionPool, initialCameraState } from '../services/cameraSessionService';

const WebRTCStream = ({ streamId, streamName, streamUrl, onError, showControls = true, expandedControls = null, dashboard = false, onStatusChange }) => {
  const videoRef = useRef(null);
  const sharedPool = useContext(CameraSessionsContext);
  const [localPool] = useState(createCameraSessionPool);
  const pool = sharedPool || localPool;
  const sessionRef = useRef(null);
  const errorCallbackRef = useRef(onError);
  errorCallbackRef.current = onError;
  const statusCallbackRef = useRef(onStatusChange);
  statusCallbackRef.current = onStatusChange;
  const [sessionState, setSessionState] = useState(initialCameraState);
  const { failed, isConnecting, isConnected } = sessionState;
  const [playbackError, setError] = useState(null);
  const error = playbackError || sessionState.error;
  const [isPiP, setIsPiP] = useState(false);
  const [pipSupported, setPipSupported] = useState(
    typeof document !== "undefined" && !!document.pictureInPictureEnabled
  );
  const [pipStatus, setPipStatus] = useState(null);

  useEffect(() => {
    statusCallbackRef.current?.(streamId, isConnected ? 'live' : failed ? 'failed' : 'connecting');
  }, [streamId, isConnected, failed]);

  const showPipStatus = (message) => {
    setPipStatus(message);
    setTimeout(() => setPipStatus(null), 2500);
  };

  const togglePictureInPicture = () => {
    const video = videoRef.current;
    if (!video) return;

    // Some browsers/platforms implicitly mark the element as PiP-ineligible
    // (e.g. via disablePictureInPicture). Force it off before every attempt.
    try {
      video.disablePictureInPicture = false;
    } catch (_) {
      /* ignore read-only environments */
    }

    console.log(`[${streamId}] PiP attempt`, {
      readyState: video.readyState,
      videoWidth: video.videoWidth,
      videoHeight: video.videoHeight,
      disablePictureInPicture: video.disablePictureInPicture,
      pictureInPictureEnabled: document.pictureInPictureEnabled,
      srcObject: video.srcObject,
      tracks: video.srcObject
        ? video.srcObject.getTracks().map((t) => ({ kind: t.kind, readyState: t.readyState, enabled: t.enabled }))
        : null,
    });

    try {
      if (document.pictureInPictureElement === video) {
        // Safari/WebKit requires requestPictureInPicture (and exit) to be
        // called synchronously within the click handler - any await before
        // it strips the user-gesture flag and the call gets rejected.
        document.exitPictureInPicture().then(() => showPipStatus("Exited floating window")).catch((err) => {
          console.error(`[${streamId}] Exit Picture-in-Picture failed:`, err);
          showPipStatus("Could not exit floating window");
        });
      } else if (typeof video.requestPictureInPicture === "function") {
        video
          .requestPictureInPicture()
          .then(() => {
            console.log(`[${streamId}] Picture-in-Picture entered`);
            showPipStatus("Floating window active");
          })
          .catch((err) => {
            console.error(`[${streamId}] Standard Picture-in-Picture failed (readyState=${video.readyState}):`, err);
            // On some Safari/iOS setups the standard API exists but rejects;
            // try the WebKit-prefixed path as a fallback.
            if (typeof video.webkitSetPresentationMode === "function") {
              try {
                video.webkitSetPresentationMode(
                  video.webkitPresentationMode === "picture-in-picture" ? "inline" : "picture-in-picture"
                );
                showPipStatus("Floating window active (WebKit)");
                return;
              } catch (webkitErr) {
                console.error(`[${streamId}] WebKit Picture-in-Picture fallback failed:`, webkitErr);
              }
            }
            const reason = video.disablePictureInPicture
              ? "PiP disabled on this video element"
              : err.message || err.name || "unknown error";
            setError(`Floating window unavailable: ${reason}`);
            showPipStatus("Floating window unavailable");
          });
      } else if (typeof video.webkitSetPresentationMode === "function") {
        // Older Safari (macOS/iPad) exposes a WebKit-prefixed API instead of
        // the standard requestPictureInPicture(). On iPhone this call often
        // succeeds silently without entering a floating window.
        const previousMode = video.webkitPresentationMode;
        video.webkitSetPresentationMode(
          previousMode === "picture-in-picture" ? "inline" : "picture-in-picture"
        );
        if (video.webkitPresentationMode === "picture-in-picture") {
          showPipStatus("Floating window active");
        } else if (previousMode === video.webkitPresentationMode) {
          showPipStatus("Floating window not supported on this device/browser");
        } else {
          showPipStatus("Floating window active (WebKit)");
        }
      } else {
        throw new Error("Picture-in-Picture is not supported in this browser/app context");
      }
    } catch (err) {
      console.error(`[${streamId}] Picture-in-Picture failed (readyState=${video.readyState}):`, err);
      setError(`Floating window unavailable: ${err.message || err.name || "unknown error"}`);
    }
  };

  const startStream = () => {
    setError(null);
    sessionRef.current?.start();
  };

  const stopStream = () => {
    setError(null);
    sessionRef.current?.stop();
  };

  useLayoutEffect(() => {
    let lastError = null;
    const video = videoRef.current;
    // Auto-start the stream when component mounts
    const session = pool.acquire(streamId, streamUrl, (state) => {
      if (video && video.srcObject !== state.mediaStream) {
        video.srcObject = state.mediaStream;
      }
      setSessionState(state);
      if (state.error && state.error !== lastError) errorCallbackRef.current?.(new Error(state.error));
      lastError = state.error;
    });
    sessionRef.current = session;
    return () => {
      sessionRef.current = null;
      session.release();
      if (video) video.srcObject = null;
    };
  }, [pool, streamId, streamUrl]);

  useEffect(() => {
    const video = videoRef.current;
    const onEnterPiP = () => setIsPiP(true);
    const onLeavePiP = () => setIsPiP(false);
    const onWebkitPresentationModeChanged = () => {
      setIsPiP(video.webkitPresentationMode === "picture-in-picture");
    };
    if (video) {
      video.addEventListener("enterpictureinpicture", onEnterPiP);
      video.addEventListener("leavepictureinpicture", onLeavePiP);
      video.addEventListener("webkitpresentationmodechanged", onWebkitPresentationModeChanged);
      if (
        !pipSupported &&
        typeof video.webkitSupportsPresentationMode === "function" &&
        video.webkitSupportsPresentationMode("picture-in-picture")
      ) {
        setPipSupported(true);
      }
    }

    return () => {
      if (video) {
        video.removeEventListener("enterpictureinpicture", onEnterPiP);
        video.removeEventListener("leavepictureinpicture", onLeavePiP);
        video.removeEventListener("webkitpresentationmodechanged", onWebkitPresentationModeChanged);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // We want this to run only once on mount

  return (
    <div className="webrtc-stream">
      {expandedControls && (
        <div className="expanded-controls">
          <div className="stream-header">
            <div className="stream-controls">
              {!isConnected && !isConnecting && (
                <button onClick={startStream} className="btn-start">
                  Start Stream
                </button>
              )}
              {isConnecting && (
                <button disabled className="btn-connecting">
                  Connecting...
                </button>
              )}
              {isConnected && (
                <button onClick={stopStream} className="btn-stop">
                  Stop Stream
                </button>
              )}
            </div>
          </div>
          
          <div className="stream-info">
            <p><strong>Stream ID:</strong> {expandedControls.streamInfo.id}</p>
            <p><strong>On Demand:</strong> {expandedControls.streamInfo.onDemand ? 'Yes' : 'No'}</p>
            <p><strong>Audio:</strong> {expandedControls.streamInfo.disableAudio ? 'Disabled' : 'Enabled'}</p>
            <p><strong>Detection:</strong> {expandedControls.streamInfo.detectionEnabled ? 'Enabled' : 'Disabled'}</p>
          </div>
          
          <div className="stream-status">
            Status:{" "}
            {isConnecting
              ? "Connecting..."
              : isConnected
              ? "Connected"
              : "Disconnected"}
          </div>
        </div>
      )}

      {error && <div className="error-message">Error: {error}</div>}

      <div className="video-container">
        {dashboard && <>
          <span className={`desktop-camera-label ${isConnected ? 'live' : ''}`}><i />{streamName || streamId}</span>
          {!isConnected && <div className="desktop-camera-state"><strong>{failed ? 'No signal' : 'Connecting…'}</strong><small>{failed ? 'Stream did not start' : 'Waiting for camera'}</small>{failed && <button type="button" onPointerDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); startStream(); }}>Retry</button>}</div>}
          {isConnected && <button type="button" className="desktop-fullscreen" aria-label={`Fullscreen ${streamName || streamId}`} onPointerDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); videoRef.current.parentElement.requestFullscreen?.().catch(() => showPipStatus('Fullscreen unavailable')); }}>⛶</button>}
        </>}
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          controls
          style={{
            width: "100%",
            maxWidth: "100%",
            height: "auto",
            backgroundColor: "#000",
            display: "block",
            margin: 0,
            padding: 0,
          }}
        />
        {pipSupported && isConnected && (
          <button
            type="button"
            className={`pip-toggle-btn ${isPiP ? "active" : ""}`}
            onPointerDown={(e) => { if (dashboard) e.stopPropagation(); }}
            onClick={(e) => { if (dashboard) e.stopPropagation(); togglePictureInPicture(); }}
            title={isPiP ? "Exit floating window" : "Play in floating window"}
            aria-label={isPiP ? "Exit floating window" : "Play in floating window"}
          >
            <svg viewBox="0 0 24 24" width="18" height="18">
              <rect x="3" y="4" width="18" height="16" rx="2" fill="none" stroke="currentColor" strokeWidth="1.6" />
              <rect x="12" y="12" width="7" height="5" rx="1" fill="currentColor" />
            </svg>
          </button>
        )}
        {pipStatus && (
          <div className={`pip-status-toast ${pipStatus ? "visible" : ""}`}>{pipStatus}</div>
        )}
      </div>
    </div>
  );
};

export default WebRTCStream;
